import React, { useContext } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { DISABLED_OPTION_PLACHOLDER_ID } from '../../../config/constants';
import { IconElements } from '../../../config/icons';
import { PensionContext, PensionParameters } from '../../../contexts/pension-robot/PensionContext';
import { DeepPartial } from '../../../utils/typings';
import { YesNoFormGroup } from '../../general/form-inputs/YesNoFormGroup';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';

export type QuestionType = 'money' | 'select' | 'switch' | 'text' | 'money-unlockable';

export type InfoType = {
    title: string;
    tooltip?: { title: string; text: string };
    type: QuestionType;
    select?: { options: { label: string; value: string }[] };
    placeholder?: string;
    show?: (parameters: PensionParameters['expensesInfo']) => boolean;
    get: (parameters: PensionParameters['expensesInfo']) => string | undefined;
    onChange: (value: string, parameters?: PensionParameters['expensesInfo']) => DeepPartial<PensionParameters>;
};

export const renderOptionsForSelect = (options: {value: string, label: string}[] ) => {
    
    const getAvailableOptions = () => { 
        return options.map((option) => {
            return (
                <option value={option.value} key={option.value}>
                    {option.label}
                </option>
            );
        })
    }
    return <>
        <option value={DISABLED_OPTION_PLACHOLDER_ID} disabled>Selecteer...</option>
        {getAvailableOptions()}
    </>
}

export const NibudField: React.FC<{ info: InfoType }> = ({ info }) => {
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);

    if (info.show !== undefined && !info.show(expensesInfo)) {
        return <></>;
    }

    return (
        <div className="mb-6 ml-4 flex flex-row gap-2 sm:gap-4 flex-wrap sm:flex-nowrap justify-between items-start">
            <div className="mr-2 w-52 sm:w-60 items-center">
                {info.title}
            </div>
            {info.type === 'money' && (
                <CurrencyInput
                    type="text"
                    placeholder={info.placeholder}
                    className={'w-56 sm:w-64 py-2 px-4 border border-gray-200 bg-gray-100 rounded-lg focus:outline-none shadow-inner'}
                    fixedDecimalLength={0}
                    decimalsLimit={2}
                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                    allowDecimals={false}
                    value={!!info.get(expensesInfo) ? info.get(expensesInfo) : ''}
                    onValueChange={(value = '', _) => actions.setParameters(info.onChange(value, expensesInfo))}
                />
            )}
            {info.type === 'select' && (
                <span className="shadow-inner relative flex flex-row items-center pl-4 py-0.5 border rounded-lg bg-gray-100">
                    <select
                        className="w-52 sm:w-60 px-0 py-1.5 pr-10 focus:outline-none appearance-none bg-opacity-0 bg-gray-100 cursor-pointer"
                        value={info.get(expensesInfo)}
                        onChange={(e) => {
                            actions.setParameters(info.onChange(e.target.value, expensesInfo));
                        }}
                    >
                        {info.select?.options ? renderOptionsForSelect(info.select?.options) : ""}
                    </select>
                    <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
                </span>
            )}
            {info.type === 'switch' && (
                <YesNoFormGroup
                    className="w-56 sm:w-64"
                    value={
                        info.get(expensesInfo) === undefined ? null : info.get(expensesInfo) === 'true' ? 'yes' : 'no'
                    }
                    onChange={(value) => {
                        if (value) {
                            actions.setParameters(info.onChange(value, expensesInfo));
                        }
                    }}
                />
            )}
            {info.type === 'text' && (
                <CurrencyInput
                    type="text"
                    placeholder={info.placeholder}
                    className={'w-56 sm:w-64 py-2 px-4 border border-gray-200 bg-gray-100 rounded-lg focus:outline-none shadow-inner'}
                    fixedDecimalLength={0}
                    prefix=""
                    suffix=""
                    maxLength={9}
                    allowDecimals={false}
                    value={info.get(expensesInfo)}
                    onValueChange={(value = '', _) => actions.setParameters(info.onChange(value, expensesInfo))}
                />
            )}
            {info.type === 'money-unlockable' && (
                <CurrencyInput
                    type="text"
                    placeholder={info.placeholder}
                    className={'w-56 sm:w-64 py-2 px-4 border border-gray-200 bg-gray-100 rounded-lg focus:outline-none shadow-inner'}
                    fixedDecimalLength={0}
                    decimalsLimit={2}
                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                    allowDecimals={false}
                    value={!!info.get(expensesInfo) ? info.get(expensesInfo) : ''}
                    onValueChange={(value = '', _) => actions.setParameters(info.onChange(value, expensesInfo))}
                />
            )}
            <div style={{ flex: 2 }}>
                {!!info.tooltip && (
                    <Tooltip
                        className="w-10"
                        tooltipId={info.tooltip.title}
                        tooltipTitle={info.tooltip.title}
                        tooltipText={info.tooltip.text}
                    >
                        <TooltipIndicator>?</TooltipIndicator>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};
